/* eslint-disable max-lines-per-function, max-statements */
import { getRouterSelectors } from '@ngrx/router-store';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import type { LocalBusiness, WithContext } from 'schema-dts';
import type { Dealer, VehicleMarketDealer } from '../../api/api-types';
import type { Loadable, Loaded } from '../../guards/ngrx-loader.guard';
import { actions } from './dealer.actions';
const { selectRouteParam, selectRouteDataParam } = getRouterSelectors();

interface DealerState {
    dealer: Loadable<Dealer>;
    vehicleMarketDealer: Loadable<VehicleMarketDealer>;
}

const initialState: DealerState = {
    dealer: null,
    vehicleMarketDealer: null,
};

export const state = createFeature({
    name: 'dealer',
    reducer: createReducer(
        initialState,
        on(
            actions.loadDealerBySlugSuccess,
            (state, { dealer }): DealerState => ({
                ...state,
                dealer,
            }),
        ),
        on(
            actions.loadDealerBySlugFailure,
            (state): DealerState => ({
                ...state,
                dealer: false,
            }),
        ),
        on(
            actions.loadVehicleMarketDealerBySlugSuccess,
            (state, { dealer }): DealerState => ({
                ...state,
                vehicleMarketDealer: dealer,
            }),
        ),
        on(
            actions.loadVehicleMarketDealerBySlugFailure,
            (state): DealerState => ({
                ...state,
                vehicleMarketDealer: false,
            }),
        ),
    ),
    extraSelectors: (baseSelectors) => {
        const selectIsLoadingDealer = createSelector(baseSelectors.selectDealer, (dealer) => dealer === null);
        const selectIsLoadingVehicleMarketDealer = createSelector(
            baseSelectors.selectVehicleMarketDealer,
            (dealer) => dealer === null,
        );

        const selectDealerOrVehicleMarketDealer = createSelector(
            baseSelectors.selectDealer,
            baseSelectors.selectVehicleMarketDealer,
            (dealer, vehicleMarketDealer) => dealer || vehicleMarketDealer || null,
        );

        //!!! Nur in Partner Web nutzen
        const selectLoadedDealer = createSelector(baseSelectors.selectDealer, (dealer) => dealer as Loaded<Dealer>);
        const selectDealerSlug = createSelector(selectLoadedDealer, (dealer) => dealer.slug);
        const selectServicesOpening = createSelector(selectLoadedDealer, (dealer) => dealer.openings.service);
        const selectHasSubDealers = createSelector(
            selectLoadedDealer,
            (dealer) => (dealer?.subDealers ?? []).length > 0,
        );
        const selectKeyCampaign = createSelector(selectLoadedDealer, (dealer) => dealer.keyCampaign);
        const selectIsSubDealerAware = createSelector(
            selectRouteDataParam('isSubDealerAware'),
            (isSubDealerAware) => !!isSubDealerAware,
        );
        const selectSubDealerOrDealer = createSelector(
            selectLoadedDealer,
            selectHasSubDealers,
            selectRouteParam('subDealerSlug'),
            selectRouteDataParam('isSubDealerAware'),
            (dealer, hasSubDealers, subDealerSlug, isSubDealerAware) => {
                let parentDealerSlug: string | null = null;
                if (!hasSubDealers || !isSubDealerAware || !subDealerSlug) {
                    return {
                        ...dealer,
                        parentDealerSlug,
                    };
                }
                const subdealer = dealer.subDealers.find((subDealer) => subDealer.slug === subDealerSlug);
                const isSubDealer = !!subdealer;
                parentDealerSlug = isSubDealer ? dealer.slug : null;
                return {
                    ...dealer,
                    ...subdealer,
                    isSubDealer,
                    parentDealerSlug,
                };
            },
        );
        const selectDealerContactFormUrl = createSelector(
            selectSubDealerOrDealer,
            (dealer) => dealer.pages.contactForm ?? '',
        );

        const selectDealerServiceFormUrl = createSelector(
            selectSubDealerOrDealer,
            (dealer) => dealer.pages.serviceForm ?? '',
        );

        const selectDealerLocationHeadline = createSelector(
            selectSubDealerOrDealer,
            (dealer) =>
                `Bei Ihrem ${dealer.dealerType === 'Service' ? 'Werkstatt-Partner' : 'Partner'} ${dealer.name} in ${dealer.city}.`,
        );

        const selectSubDealerOrDealerSlug = createSelector(
            selectSubDealerOrDealer,
            selectRouteParam('slug'),
            (dealer, slug) => dealer?.slug ?? slug ?? '',
        );

        const selectSubDealerOrDealerOrVehicleMarketDealerSlug = createSelector(
            selectDealerOrVehicleMarketDealer,
            selectSubDealerOrDealerSlug,
            (dealerOrVehicleMarketDealer, dealerOrSubDealer) =>
                dealerOrSubDealer || dealerOrVehicleMarketDealer?.slug || '',
        );

        const selectHyundaiPromiseLink = createSelector(selectLoadedDealer, (dealer) => dealer.pages.hyundaiPromise);
        const selectDealerName = createSelector(selectLoadedDealer, (dealer) =>
            dealer ? [dealer.name, dealer.name2, dealer.name3].filter((n) => !!n.trim()).join(' ') : '',
        );
        const selectSocialLinks = createSelector(selectLoadedDealer, (dealer) => {
            const links: string[] = [];

            if (dealer.socials.facebook) links.push(dealer.socials.facebook);
            if (dealer.socials.instagram) links.push(dealer.socials.instagram);
            if (dealer.socials.linkedIn) links.push(dealer.socials.linkedIn);
            if (dealer.socials.tiktok) links.push(dealer.socials.tiktok);
            if (dealer.socials.youtube) links.push(dealer.socials.youtube);
            if (dealer.socials.x) links.push(dealer.socials.x);

            return links;
        });
        const selectRichDealerData = createSelector(selectLoadedDealer, selectSocialLinks, (dealer, socialLinks) => {
            return {
                '@context': 'https://schema.org',
                '@type': 'LocalBusiness',
                name: dealer.name,
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: dealer.street,
                    addressLocality: dealer.city,
                    postalCode: dealer.zip,
                    addressRegion: dealer.state,
                    addressCountry: 'DE',
                },
                url: `https://hyundai-partners.de/${dealer.slug}`,
                telephone: dealer.phone,
                openingHoursSpecification: dealer.openings.overallRange
                    ? {
                          '@type': 'OpeningHoursSpecification',
                          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                          opens: dealer.openings.overallRange.start,
                          closes: dealer.openings.overallRange.end,
                      }
                    : undefined,
                sameAs: socialLinks,
            } satisfies WithContext<LocalBusiness>;
        });

        const selectShowImprintNotice = createSelector(
            selectRouteDataParam('hideImprintNotice'),
            (hideImprintNotice) => !hideImprintNotice,
        );

        return {
            selectIsLoadingDealer,
            selectLoadedDealer,
            selectSubDealerOrDealer,
            selectDealerSlug,
            selectServicesOpening,
            selectDealerLocationHeadline,
            selectHasSubDealers,
            selectKeyCampaign,
            selectSubDealerOrDealerSlug,
            selectIsSubDealerAware,
            selectHyundaiPromiseLink,
            selectDealerName,
            selectDealerContactFormUrl,
            selectDealerServiceFormUrl,
            selectSocialLinks,
            selectRichDealerData,
            selectIsLoadingVehicleMarketDealer,
            selectDealerOrVehicleMarketDealer,
            selectSubDealerOrDealerOrVehicleMarketDealerSlug,
            selectShowImprintNotice,
        };
    },
});
